/**
 * Lookup Settings Hooks
 *
 * React Query hooks for managing lookup entities in Settings:
 * Job Titles, Disciplines, Skills, and Leave Types.
 */

import { queryKeys } from '@/lib/query-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/lookupSettingsApi';

// ---------------------------------------------------------------------------
// Job Titles
// ---------------------------------------------------------------------------

export function useJobTitlesSettings() {
  return useQuery({
    queryKey: queryKeys.settings.jobTitles(),
    queryFn: () => api.getJobTitles(),
    staleTime: 60 * 1000,
  });
}

export function useCreateJobTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobTitle: string) => api.createJobTitle(jobTitle),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobTitles() });
    },
    meta: { resourceName: 'Job Title', action: 'create' },
  });
}

export function useUpdateJobTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, jobTitle }: { id: number; jobTitle: string }) =>
      api.updateJobTitle(id, jobTitle),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobTitles() });
    },
    meta: { resourceName: 'Job Title', action: 'update' },
  });
}

export function useDeleteJobTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteJobTitle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.jobTitles() });
    },
    meta: { resourceName: 'Job Title', action: 'delete' },
  });
}

// ---------------------------------------------------------------------------
// Disciplines
// ---------------------------------------------------------------------------

export function useDisciplinesSettings() {
  return useQuery({
    queryKey: queryKeys.settings.disciplines(),
    queryFn: () => api.getDisciplines(),
    staleTime: 60 * 1000,
  });
}

export function useCreateDiscipline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { discipline: string; code?: string }) => api.createDiscipline(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.disciplines() });
    },
    meta: { resourceName: 'Discipline', action: 'create' },
  });
}

export function useUpdateDiscipline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { discipline: string; code?: string } }) =>
      api.updateDiscipline(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.disciplines() });
    },
    meta: { resourceName: 'Discipline', action: 'update' },
  });
}

export function useDeleteDiscipline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteDiscipline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.disciplines() });
    },
    meta: { resourceName: 'Discipline', action: 'delete' },
  });
}

// ---------------------------------------------------------------------------
// Skills
// ---------------------------------------------------------------------------

export function useSkillsSettings(disciplineId?: number) {
  return useQuery({
    queryKey: [...queryKeys.settings.skills(), disciplineId] as const,
    queryFn: () => api.getSkills(disciplineId),
    staleTime: 60 * 1000,
  });
}

export function useCreateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; discipline_id?: number }) => api.createSkill(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.skills() });
    },
    meta: { resourceName: 'Skill', action: 'create' },
  });
}

export function useUpdateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; name: string; discipline_id?: number }) =>
      api.updateSkill(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.skills() });
    },
    meta: { resourceName: 'Skill', action: 'update' },
  });
}

export function useDeleteSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteSkill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.skills() });
    },
    meta: { resourceName: 'Skill', action: 'delete' },
  });
}

// ---------------------------------------------------------------------------
// Leave Types
// ---------------------------------------------------------------------------

export function useLeaveTypesSettings() {
  return useQuery({
    queryKey: queryKeys.settings.leaveTypes(),
    queryFn: () => api.getLeaveTypes(),
    staleTime: 60 * 1000,
  });
}

export function useCreateLeaveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (description: string) => api.createLeaveType(description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.leaveTypes() });
    },
    meta: { resourceName: 'Leave Type', action: 'create' },
  });
}

export function useUpdateLeaveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, description }: { id: number; description: string }) =>
      api.updateLeaveType(id, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.leaveTypes() });
    },
    meta: { resourceName: 'Leave Type', action: 'update' },
  });
}

export function useDeleteLeaveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteLeaveType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.leaveTypes() });
    },
    meta: { resourceName: 'Leave Type', action: 'delete' },
  });
}
